/**
 * SettingsScene - Sound toggle and how-to-play reference.
 * Returns to whichever scene opened it.
 */

import Phaser from 'phaser';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, makeButton, makePanel, paintBackground, fadeInScene } from '../constants/Theme';

export class SettingsScene extends Phaser.Scene {
  private fromScene: string = SCENES.MENU;

  constructor() {
    super({ key: SCENES.SETTINGS });
  }

  init(data: { from?: string } = {}): void {
    this.fromScene = data.from || SCENES.MENU;
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);

    const title = this.add.text(cx, 110, 'SETTINGS', T.title());
    title.setOrigin(0.5);
    title.setLetterSpacing(2);

    // Toggle rows
    const audio = AudioManager.getInstance();
    audio.init(this);

    this.makeToggleRow(cx, 218, 'Sound Effects',
      () => audio.isSoundOn(),
      (on) => { audio.setSoundOn(on); if (on) audio.play('click'); });
    this.makeToggleRow(cx, 312, 'Music',
      () => audio.isMusicOn(),
      (on) => audio.setMusicOn(on));
    this.makeToggleRow(cx, 406, 'Vibration',
      () => audio.isHapticsOn(),
      (on) => audio.setHapticsOn(on));

    // How to play card
    const cardTop = 480;
    const cardH = 470;
    makePanel(this, cx, cardTop + cardH / 2, 640, cardH, { fill: PALETTE.panel, radius: 20 });

    const helpTitle = this.add.text(cx, cardTop + 36, 'HOW TO PLAY', T.subheading({ color: INK.gold }));
    helpTitle.setOrigin(0.5);
    helpTitle.setLetterSpacing(2);

    const lines = [
      'Tap one of your (white) pieces, then tap a highlighted square to move.',
      'Reduce the enemy to just their king, or checkmate them, to win a battle.',
      'Lose all your fighting pieces and the run ends.',
      'Pick upgrades after each win and spend gold in shops to build your army.',
      'Clear all 12 floors and defeat the boss to win the run.',
    ];
    lines.forEach((line, i) => {
      const y = cardTop + 86 + i * 74;
      const bullet = this.add.text(cx - 280, y, '♟', { fontFamily: T.body().fontFamily, fontSize: '24px', color: INK.gold });
      bullet.setOrigin(0.5, 0);
      const text = this.add.text(cx - 250, y, line, T.body({
        color: INK.secondary, wordWrap: { width: 500 }, lineSpacing: 4,
      }));
      text.setOrigin(0, 0);
    });

    makeButton(this, cx, HEIGHT - 130, 'BACK', {
      fill: PALETTE.blue, onClick: () => this.scene.start(this.fromScene),
    });
  }

  /** A labelled ON/OFF toggle row. */
  private makeToggleRow(cx: number, y: number, label: string, get: () => boolean, set: (on: boolean) => void): void {
    makePanel(this, cx, y, 600, 86, { fill: PALETTE.panel, radius: 18 });

    const text = this.add.text(cx - 260, y, label, T.heading({ fontSize: '30px' }));
    text.setOrigin(0, 0.5);

    const tw = 150, th = 60;
    const toggle = this.add.container(cx + 200, y);
    const tg = this.add.graphics();
    const tText = this.add.text(0, 0, '', T.button({ fontSize: '26px' }));
    tText.setOrigin(0.5);
    toggle.add([tg, tText]);
    toggle.setSize(tw, th);
    toggle.setInteractive(new Phaser.Geom.Rectangle(-tw / 2, -th / 2, tw, th), Phaser.Geom.Rectangle.Contains);

    const draw = () => {
      const on = get();
      tg.clear();
      tg.fillStyle(on ? PALETTE.green : PALETTE.panelRaised, 1);
      tg.fillRoundedRect(-tw / 2, -th / 2, tw, th, 14);
      tg.lineStyle(2, on ? PALETTE.greenLight : PALETTE.stroke, 1);
      tg.strokeRoundedRect(-tw / 2, -th / 2, tw, th, 14);
      // knob
      const knobX = on ? tw / 2 - 22 : -tw / 2 + 22;
      tg.fillStyle(0xffffff, 0.9);
      tg.fillCircle(knobX, 0, 14);
      tText.setText(on ? 'ON' : 'OFF');
      tText.setX(on ? -22 : 22);
    };
    draw();

    toggle.on('pointerdown', () => { set(!get()); draw(); });
  }
}
